/**
 * Tenant entitlement HTTP 403 deny codes from assertUseCaseEntitled / requireEntitled.
 * Shared so shop catalog UI and checkout pay-error recovery can classify without
 * crossing feature → shared boundaries the wrong way.
 */
import {
  isClientAccessDenyKind,
  type EntitlementDenialItem,
} from 'pi-kiosk-shared';
import { ApiError } from './ApiClient.js';

export const TENANT_ENTITLEMENT_READ_DENIED = 'TENANT_ENTITLEMENT_READ_DENIED' as const;
export const TENANT_ENTITLEMENT_WRITE_DENIED = 'TENANT_ENTITLEMENT_WRITE_DENIED' as const;

function entitlementCodeOf(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    return error.code;
  }
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Package F0 — parse `details.entitlementDenials` from ApiError.data
 * (flat envelope or nested `error.details`). Preserves existing error codes;
 * unrecognized kinds / malformed rows are dropped.
 */
export function parseEntitlementDenialsFromApiErrorData(
  error: unknown,
): readonly EntitlementDenialItem[] {
  if (!(error instanceof ApiError)) {
    return [];
  }
  const root = asRecord(error.data);
  if (root === null) {
    return [];
  }
  const nestedError = asRecord(root.error);
  const details =
    asRecord(root.details) ??
    (nestedError !== null ? asRecord(nestedError.details) : null);
  if (details === null) {
    return [];
  }
  const raw = details.entitlementDenials;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: EntitlementDenialItem[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (row === null) {
      continue;
    }
    const blockKey = row.blockKey;
    const kind = row.kind;
    if (typeof blockKey !== 'string' || blockKey.trim().length === 0) {
      continue;
    }
    if (!isClientAccessDenyKind(kind)) {
      continue;
    }
    out.push({ blockKey, kind });
  }
  return out;
}

/**
 * True when the failure is a hard tenant entitlement deny (read or write).
 * Catalog: empty/unavailable in JourneyShell. Checkout create-session: access-denied recovery.
 */
export function isTenantEntitlementDeniedError(error: unknown): boolean {
  const code = entitlementCodeOf(error);
  return code === TENANT_ENTITLEMENT_READ_DENIED || code === TENANT_ENTITLEMENT_WRITE_DENIED;
}
